/**
 * Internationalization/Globalization class.
 * @dynamic
 */
export class Globalization {
    private static fullTimeOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        timeZoneName: 'short'
    };

    private static dateTimeOnlyOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric'
    };

    private static dateTimeOnlyShortOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
    };

    private static dateOnlyOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
    };

    private static timeOnlyOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric'
    };

    /**
     * Caching format objects for better performance.
     */
    private static internalFullTimeFormat: Intl.DateTimeFormat;
    private static internalDateTimeOnlyShortFormat: Intl.DateTimeFormat;
    private static internalDateTimeOnlyFormat: Intl.DateTimeFormat;
    private static internalDateOnlyFormat: Intl.DateTimeFormat;
    private static internalTimeOnlyFormat: Intl.DateTimeFormat;
    private static internalNumberFormat: Intl.NumberFormat;

    private static get fullTimeFormat(): Intl.DateTimeFormat {
        if (!Globalization.internalFullTimeFormat) {
            Globalization.internalFullTimeFormat = new Intl.DateTimeFormat(Globalization.localeId, Globalization.fullTimeOptions);
        }

        return Globalization.internalFullTimeFormat;
    }

    private static get dateTimeOnlyFormat(): Intl.DateTimeFormat {
        if (!Globalization.internalDateTimeOnlyFormat) {
            Globalization.internalDateTimeOnlyFormat = new Intl.DateTimeFormat(Globalization.localeId, Globalization.dateTimeOnlyOptions);
        }

        return Globalization.internalDateTimeOnlyFormat;
    }

    private static get dateTimeOnlyShortFormat(): Intl.DateTimeFormat {
        if (!Globalization.internalDateTimeOnlyShortFormat) {
            Globalization.internalDateTimeOnlyShortFormat = new Intl.DateTimeFormat(
                Globalization.localeId, Globalization.dateTimeOnlyShortOptions);
        }

        return Globalization.internalDateTimeOnlyShortFormat;
    }

    private static get dateOnlyFormat(): Intl.DateTimeFormat {
        if (!Globalization.internalDateOnlyFormat) {
            Globalization.internalDateOnlyFormat = new Intl.DateTimeFormat(Globalization.localeId, Globalization.dateOnlyOptions);
        }

        return Globalization.internalDateOnlyFormat;
    }

    private static get timeOnlyFormat(): Intl.DateTimeFormat {
        if (!Globalization.internalTimeOnlyFormat) {
            Globalization.internalTimeOnlyFormat = new Intl.DateTimeFormat(Globalization.localeId, Globalization.timeOnlyOptions);
        }

        return Globalization.internalTimeOnlyFormat;
    }

    private static get numberFormat(): Intl.NumberFormat {
        if (!Globalization.internalNumberFormat) {
            Globalization.internalNumberFormat = new Intl.NumberFormat(Globalization.localeId);
        }

        return Globalization.internalNumberFormat;
    }

    /**
     * Gets the locale ID for regional format.
     *
     * This ID must be used for any locale function calls.
     *
     * - String.prototype.localeCompare()
     * - Number.prototype.toLocaleString()
     * - Date.prototype.toLocaleString()
     * - Date.prototype.toLocaleDateString()
     * - Date.prototype.toLocaleTimeString()
     *
     * @return string the local ID.
     */
    public static get localeId(): string {
        const resources = MsftSme.self().Resources;
        const id = resources.localeRegionalId;
        return id ? id : null;
    }

    /**
     * Format with full time to display.
     *
     * @param data the date object.
     */
    public static fullTime(date: Date): string {
        if (MsftSme.isNullOrUndefined(date)) {
            return null;
        }

        return Globalization.fullTimeFormat.format(date);
    }

    /**
     * Format with date time only to display.
     *
     * @param data the date object.
     */
    public static dateTimeOnly(date: Date): string {
        if (MsftSme.isNullOrUndefined(date)) {
            return null;
        }

        return Globalization.dateTimeOnlyFormat.format(date);
    }

    /**
     * Format with date time only without seconds to display.
     *
     * @param data the date object.
     */
    public static dateTimeOnlyShort(date: Date): string {
        if (MsftSme.isNullOrUndefined(date)) {
            return null;
        }

        return Globalization.dateTimeOnlyShortFormat.format(date);
    }

    /**
     * Format with date only to display.
     *
     * @param data the date object.
     */
    public static dateOnly(date: Date): string {
        if (MsftSme.isNullOrUndefined(date)) {
            return null;
        }

        return Globalization.dateOnlyFormat.format(date);
    }

    /**
     * Format with time only to display.
     *
     * @param data the date object.
     */
    public static timeOnly(date: Date): string {
        if (MsftSme.isNullOrUndefined(date)) {
            return null;
        }

        return Globalization.timeOnlyFormat.format(date);
    }

    /**
     * Format simple number to display.
     *
     * (see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat to add
     * more feature such as currency display)
     *
     * @param data the number data.
     * @param options the number formatting options.
     */
    public static number(data: number, options?: Intl.NumberFormatOptions): string {
        if (!options) {
            return Globalization.numberFormat.format(data);
        }

        return Intl.NumberFormat(Globalization.localeId, options).format(data);
    }
}
